import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store";
import { isAdmin, isCompanyOwner, isCompanySpecialist } from "../../utils/permissions";
import { useTranslation } from "react-i18next";
import companiesService, { Company } from "../../features/companies/companiesService";
import { useDispatch } from "react-redux";
import { setSelectedClient } from "../../features/settings/settingsSlice";

const AIToolsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useAppSelector((s) => s.auth);
  const canView = isAdmin(user) || isCompanyOwner(user) || isCompanySpecialist(user);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [query, setQuery] = useState("");
  const [singleCompanies, setSingleCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | "">("");

  // Load single-type companies for Admin / Super Admin
  useEffect(() => {
    const load = async () => {
      if (!isAdmin(user)) return;
      const { companies } = await companiesService.getCompanies({ company_type: "single", limit: 200 });
      setSingleCompanies(companies || []);
    };
    load();
  }, [user]);

  const tools = useMemo(
    () => [
      {
        key: "ai-chatbots",
        title: t("ai_chatbots"),
        description: t("manage_your_ai_powered_chatbot_assistants"),
        icon: "bi-robot",
        to: "/ai-tools/ai-chatbots",
        colorFrom: "#3b82f6",
        colorTo: "#8b5cf6",
        category: "Chatbots",
        badges: ["Popular"],
        featured: true,
      },
      {
        key: "deep-research",
        title: t("deep_research"),
        description: t("deep_research_subtitle"),
        icon: "bi-search",
        to: "/ai-tools/deep-research",
        colorFrom: "#10b981",
        colorTo: "#14b8a6",
        category: "Research",
        badges: ["New"],
      },
      {
        key: "whatsapp-chatbots",
        title: t("whatsapp_chatbots"),
        description: t("whatsapp_chatbots_subtitle"),
        icon: "bi-whatsapp",
        to: "/ai-tools/whatsapp-chatbots",
        colorFrom: "#22c55e",
        colorTo: "#16a34a",
        category: "Messaging",
        badges: ["Beta"],
      },
    ],
    [t]
  );

  const categories = useMemo(() => {
    const set = new Set<string>(["All"]);
    tools.forEach((tool: any) => tool.category && set.add(tool.category));
    return Array.from(set);
  }, [tools]);

  const [activeCategory, setActiveCategory] = useState<string>("All");

  if (!canView) {
    return (
      <div className="ai-tools-page">
        <div className="page-header">
          <div className="header-left">
            <div className="header-icon">
              <i className="bi bi-cpu-fill" />
            </div>
            <div className="header-text">
              <h1>{t("ai_tools")}</h1>
              <p>{t("are_you_sure")}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const filtered = tools.filter((tool: any) => {
    const matchQuery =
      tool.title.toLowerCase().includes(query.toLowerCase()) ||
      tool.description.toLowerCase().includes(query.toLowerCase());
    const matchCategory = activeCategory === "All" || tool.category === activeCategory;
    return matchQuery && matchCategory;
  });

  return (
    <div className="ai-tools-page">
      <div className="page-header">
        <div className="header-left">
          <div className="header-icon">
            <i className="bi bi-cpu-fill" />
          </div>
          <div className="header-text">
            <h1>{t("ai_tools")}</h1>
            <p>{t("ai_tools_subtitle")}</p>
          </div>
        </div>
        <div className="header-right">
          {isAdmin(user) && (
            <div>
              <select
                className="form-select company-select"
                value={selectedCompanyId}
                onChange={(e) => {
                  const val = e.target.value ? Number(e.target.value) : "";
                  setSelectedCompanyId(val);
                  (dispatch as any)(setSelectedClient(typeof val === 'number' ? val : undefined));
                }}
              >
                <option value="">{t("select_company") || "Select company"}</option>
                {singleCompanies.map((c) => (
                  <option key={c.id} value={c.id}>{c.company_name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="search-box">
            <i className="bi bi-search" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("tools_search_placeholder") as string}
              aria-label={t("tools_search_placeholder") as string}
            />
            {query && (
              <button
                type="button"
                className="clear-btn"
                aria-label={t("clear") as string || "Clear"}
                onClick={() => setQuery("")}
              >
                <i className="bi bi-x-lg" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Category chips */}
      <div className="chip-group" role="tablist" aria-label="Tool categories">
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            className={`chip ${activeCategory === cat ? 'active' : ''}`}
            onClick={() => setActiveCategory(cat)}
            role="tab"
            aria-selected={activeCategory === cat}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="tools-grid" role="list">
        {filtered.map((tool: any) => (
          <button
            key={tool.key}
            className={`tool-card ${tool.featured ? 'featured' : ''}`}
            onClick={() => navigate(tool.to)}
            role="listitem"
          >
            {!!tool.badges?.length && (
              <div className="tool-badges">
                {tool.badges.map((b: string) => (
                  <span className="tool-badge" key={b}>{b}</span>
                ))}
              </div>
            )}
            <div
              className="tool-icon"
              style={{ ['--tool-gradient' as any]: `linear-gradient(135deg, ${tool.colorFrom}, ${tool.colorTo})` }}
            >
              <i className={`bi ${tool.icon}`} />
            </div>
            <div className="tool-content">
              <div className="tool-title">{tool.title}</div>
              <div className="tool-desc">{tool.description}</div>
            </div>
            <div className="tool-arrow">
              <i className="bi bi-arrow-right" />
            </div>
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="empty-state" style={{ marginTop: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: 48, opacity: 0.4, marginBottom: 8 }}>
            <i className="bi bi-stars" />
          </div>
          <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{t('no_results') || 'No results'}</div>
          <div style={{ marginTop: 6 }}>{t('try_different_filters') || 'Try different filters or search terms.'}</div>
        </div>
      )}

    </div>
  );
};

export default AIToolsPage;


